import { FC, useMemo } from 'react';
import { Input } from '@components/atoms/Input';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { Button } from '@components/atoms/Button';
import { Receipt, WarningAmber } from '@mui/icons-material';
import formatDate from '@utils/formatDate';
import { useSubscriptionPlans } from '@hooks/api/subscription-plans';
import { useTranslation } from '@hooks/useTranslation';
import { SubscriptionPlanTemplate } from '@models/subscription';

interface PlansErrorStateProps {
  message?: string;
  onRetry: () => void;
  isRetrying: boolean;
}

// Local, single-use error panel (story-215): a failed list query must never render as the empty
// table, which would read as "there are no plans".
const PlansErrorState: FC<PlansErrorStateProps> = ({ message, onRetry, isRetrying }) => {
  const tCommon = useTranslation('common');
  return (
    <div role="alert" className="flex flex-col items-center justify-center py-12 gap-2">
      <WarningAmber
        aria-hidden="true"
        className="text-amber-500 dark:text-amber-400 mb-2"
        sx={{ fontSize: 64 }}
      />
      <p className="text-gray-900 dark:text-gray-100 text-lg font-medium">
        {tCommon.couldNotLoadSubscriptionPlans}
      </p>
      {message && <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>}
      <div className="mt-4">
        <Button variant="secondary" onClick={onRetry} loading={isRetrying}>
          {tCommon.retry}
        </Button>
      </div>
    </div>
  );
};

interface SubscriptionPlansListProps {
  search: string;
  filter: string;
  orderBy: string | null;
  orderDirection: 'asc' | 'desc';
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFilterChange: (filter: string) => void;
  handleRequestSort: (property: string) => void;
  onEditPlan: (plan: SubscriptionPlanTemplate) => void;
}

const SubscriptionPlansList: FC<SubscriptionPlansListProps> = ({
  search,
  filter,
  orderBy,
  orderDirection,
  handleSearchChange,
  handleFilterChange,
  handleRequestSort,
  onEditPlan,
}) => {
  // Fetch data using React Query hook
  const plansQuery = useSubscriptionPlans();

  // Filter options for ReactSelect
  const filterOptions: SelectOption[] = [
    { value: 'ALL', label: 'All Plans' },
    { value: 'PUBLIC', label: 'Public Only' },
    { value: 'PRIVATE', label: 'Private Only' },
    { value: 'PLAN', label: 'Plans Only' },
    { value: 'ADDON', label: 'Add-ons Only' },
  ];

  // Client-side filtering and sorting
  const filteredAndSortedPlans = useMemo(() => {
    if (!plansQuery.data) return [];

    let filtered = [...plansQuery.data];

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (plan) =>
          plan.name.toLowerCase().includes(searchLower) ||
          plan.payMethod?.toLowerCase().includes(searchLower),
      );
    }

    // Apply type filter
    if (filter !== 'ALL') {
      switch (filter) {
        case 'PUBLIC':
          filtered = filtered.filter((plan) => plan.isPublic);
          break;
        case 'PRIVATE':
          filtered = filtered.filter((plan) => !plan.isPublic);
          break;
        case 'PLAN':
          filtered = filtered.filter((plan) => !plan.isAddOn);
          break;
        case 'ADDON':
          filtered = filtered.filter((plan) => plan.isAddOn);
          break;
      }
    }

    // Apply sorting
    if (orderBy) {
      filtered.sort((a: any, b: any) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        let comparison = 0;
        if (typeof aValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else if (typeof aValue === 'number') {
          comparison = aValue - bValue;
        } else if (aValue instanceof Date) {
          comparison = aValue.getTime() - bValue.getTime();
        }

        return orderDirection === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [plansQuery.data, search, filter, orderBy, orderDirection]);

  // Sortable header cell helper
  const sortedTableHeaderCell = ({
    label,
    property,
    align,
  }: {
    label: string;
    property: string;
    align?: 'right' | 'left' | 'center';
  }) => {
    const alignClass =
      align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
    const sortIcon = orderBy === property ? (orderDirection === 'asc' ? ' ↑' : ' ↓') : '';

    return (
      <th
        className={`px-6 py-3 ${alignClass} text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600`}
        onClick={() => handleRequestSort(property)}>
        {label}
        {sortIcon}
      </th>
    );
  };

  // Format price in dollars
  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const count = filteredAndSortedPlans.length;

  return (
    <div className="w-full">
      {/* Search and Filter Bar */}
      <div className="flex items-center mb-4 gap-4">
        <div className="min-w-[250px]">
          <Input
            type="text"
            placeholder="Search by name or pay method..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        <div className="min-w-[200px]">
          <ReactSelect
            value={filterOptions.find((item) => item.value === filter) || null}
            onChange={(value) => {
              const selectedValue = value as SelectOption | null;
              handleFilterChange(selectedValue?.value?.toString() || 'ALL');
            }}
            options={filterOptions}
            isClearable={false}
            isSearchable={false}
          />
        </div>
      </div>

      {/* Loading State */}
      {plansQuery.isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 dark:border-purple-400"></div>
        </div>
      ) : plansQuery.isError ? (
        /* Error State: wins over empty */
        <PlansErrorState
          message={(plansQuery.error as any)?.response?.errors?.[0]?.message}
          onRetry={() => plansQuery.refetch()}
          isRetrying={plansQuery.isRefetching}
        />
      ) : count === 0 ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-12">
          <Receipt className="text-gray-400 dark:text-gray-500 mb-4" sx={{ fontSize: 64 }} />
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {search ? 'No plans found matching your search.' : 'No subscription plans found.'}
          </p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {sortedTableHeaderCell({ label: 'Name', property: 'name' })}
                  {sortedTableHeaderCell({ label: 'Base Price', property: 'basePriceCents' })}
                  {sortedTableHeaderCell({ label: 'Included Seats', property: 'includedSeats' })}
                  {sortedTableHeaderCell({ label: 'Trial Days', property: 'trialDurationDays' })}
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Card at Sign-up
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Visibility
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  {sortedTableHeaderCell({ label: 'Created', property: 'createdAt' })}
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredAndSortedPlans.map((plan) => (
                  <tr
                    key={plan.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {plan.name}
                        </span>
                        {plan.payMethod && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {plan.payMethod}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {formatPrice(plan.basePriceCents)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 text-center">
                      {plan.includedSeats}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 text-center">
                      {plan.trialDurationDays || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          plan.requiresPaymentUpfront === false
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                        {plan.requiresPaymentUpfront === false ? 'Card-free' : 'Required'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          plan.isPublic
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                        {plan.isPublic ? 'Public' : 'Private'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          plan.isAddOn
                            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                        {plan.isAddOn ? 'Add-on' : 'Plan'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {formatDate(plan.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <Button variant="tertiary" size="md" onClick={() => onEditPlan(plan)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Results count */}
          <div className="flex items-center justify-end px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 mt-4 rounded-lg">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Showing {count} {count === 1 ? 'plan' : 'plans'}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionPlansList;
